import axios from "axios";
import { handleApiError } from "./utils/handleApiError";
import { env } from "util/env";

const ZOHO = window.ZOHO;

const flattenObj = (ob, seperator = ".") => {
  // The object which contains the
  // final result
  let result = {};

  // loop through the object "ob"
  for (const i in ob) {
    // We check the type of the i using
    // typeof() function and recursively
    // call the function again
    if (typeof ob[i] === "object") {
      const temp = flattenObj(ob[i], seperator);
      for (const j in temp) {
        // Store temp in result
        result[i + seperator + j] = temp[j];
      }
    }

    // Else store ob[i] in result directly
    else {
      result[i] = ob[i];
    }
  }
  return result;
};

const getCrmFieldNames = async (entity) => {
  const fieldResp = await ZOHO.CRM.META.getFields({
    Entity: entity,
  });

  return fieldResp?.fields;
};

const getCrmRecordData = async (entity, entityId, displayName = false) => {
  const recordResp = await ZOHO.CRM.API.getRecord({
    Entity: entity,
    approved: "both",
    RecordID: entityId,
  });
  if (displayName === true) {
    const fieldNames = await getCrmFieldNames(entity);
    let returnFieldNameValues = {};
    fieldNames.forEach((indvFieldMap) => {
      returnFieldNameValues[indvFieldMap?.field_label] = recordResp?.data?.[0]?.[indvFieldMap?.api_name];
    });
    return returnFieldNameValues;
  }
  return recordResp?.data?.[0];
};

const getCrmUsersData = async (entityId, displayName = false) => {
  const recordResp = await ZOHO.CRM.API.getRecord({
    Entity: "users",
    RecordID: entityId,
  });
  if (displayName === true) {
    const fieldNames = await getCrmFieldNames("users");
    let returnFieldNameValues = {};
    fieldNames.forEach((indvFieldMap) => {
      returnFieldNameValues[indvFieldMap?.field_label] = recordResp?.users?.[0]?.[indvFieldMap?.api_name];
    });
    return returnFieldNameValues;
  }
  return recordResp?.users?.[0];
};

export const getFieldsAndData = async ({
  entity,
  entityId = null,
  displayName = false,
  apiName = true,
  flatten = ["recordData"],
  returnValues = ["recordData"],
  fetchAdditionalFieldTypes = ["lookup", "ownerlookup"],
  // flatten = ["fields", "recordData"] or [],
  // returnValues = ["fields", "recordData"],
  // fetchAdditionalFieldTypes = ["lookup", "ownerlookup, "subform"],
}) => {
  // this function uses "flattenObj","getCrmRecordData","getCrmFieldNames","getCrmUsersData"
  try {
    let returnData = { recordData: {}, fieldNames: {} };

    if (entityId) {
      const respData = await getCrmRecordData(entity, entityId, displayName);
      if (respData === undefined) {
        return { error: "Provided Record ID is wrong.", data: null };
      }
      returnData.recordData[entity] = respData;
    }

    const respField = await getCrmFieldNames(entity);
    returnData.fieldNames[entity] = respField;
    const promises = respField?.map(async (fieldMap, index) => {
      if (fieldMap.data_type === "lookup" && fetchAdditionalFieldTypes.includes("lookup") && returnValues.includes("recordData")) {
        const lookupData = await getCrmRecordData(
          fieldMap.lookup.module.api_name,
          returnData?.recordData?.[entity]?.[fieldMap?.api_name]?.id,
          displayName
        );

        if (lookupData === undefined) {
          // Return is necessary for Promise.all()
          return "lookupFields";
        }

        if (displayName) {
          returnData.recordData[entity][fieldMap.field_label] = lookupData;
        }

        if (apiName) {
          returnData.recordData[entity][fieldMap.api_name] = lookupData;
        }

        // Return is necessary for Promise.all()
        return "lookupFields";
      }

      if (fieldMap.data_type === "lookup" && fetchAdditionalFieldTypes.includes("lookup") && returnValues.includes("fields")) {
        const lookupFields = await getCrmFieldNames(fieldMap?.lookup?.module?.api_name);
        if (lookupFields === undefined) {
          /**
           * Return is necessary for Promise.all()
           */
          return "lookupFields";
        }
        const modifiedLookupFields = lookupFields.map((indvField, index) => {
          return {
            ...indvField,
            moduleName: fieldMap.field_label,
            lookupfield_api_name: `${entity}.${fieldMap.api_name}`,
            fieldOriginType: "lookupfield",
          };
        });
        // Using Display Name
        if (displayName) {
          returnData.fieldNames[fieldMap.field_label] = modifiedLookupFields;
        }
        // Using API Name
        if (apiName) {
          returnData.fieldNames[fieldMap.api_name] = modifiedLookupFields;
        }

        /**
         * Return is necessary for Promise.all()
         */
        return "lookupFields";
      }

      if (
        fieldMap.data_type === "ownerlookup" &&
        fetchAdditionalFieldTypes.includes("ownerlookup") &&
        returnValues.includes("recordData")
      ) {
        //  For Owner Lookup Fields Adding User Data
        const lookupData = await getCrmUsersData(returnData.recordData[entity][fieldMap.api_name]?.id, displayName);

        if (lookupData === undefined) {
          // Return is necessary for Promise.all()
          return "lookupFields";
        }

        if (displayName) {
          returnData.recordData[entity][fieldMap.field_label] = lookupData;
        }

        if (apiName) {
          returnData.recordData[entity][fieldMap.api_name] = lookupData;
        }

        // Return is necessary for Promise.all()
        return "lookupFields";
      }

      if (fieldMap.data_type === "ownerlookup" && fetchAdditionalFieldTypes.includes("ownerlookup") && returnValues.includes("fields")) {
        //  For Owner Lookup Fields Adding USer Fields
        const lookupFields = await getCrmFieldNames("users");
        if (lookupFields === undefined) {
          // Return is necessary for Promise.all()
          return "lookupFields";
        }

        const modifiedLookupFields = lookupFields.map((indvField, index) => {
          return {
            ...indvField,
            moduleName: fieldMap.field_label,
            lookupfield_api_name: `${entity}.${fieldMap.api_name}`,
          };
        });

        if (displayName) {
          returnData.fieldNames[fieldMap.field_label] = modifiedLookupFields;
        }

        if (apiName) {
          returnData.fieldNames[fieldMap.api_name] = modifiedLookupFields;
        }

        // Return is necessary for Promise.all()
        return "lookupFields";
      }

      if (fieldMap.data_type === "subform" && fetchAdditionalFieldTypes.includes("subform") && returnValues.includes("fields")) {
        const lookupFields = await getCrmFieldNames(fieldMap.subform.module);
        if (lookupFields === undefined) {
          return "lookupFields";
        }
        const modifiedLookupFields = lookupFields.map((indvField, index) => {
          return {
            ...indvField,
            moduleName: fieldMap.field_label,
            fieldOriginType: "subform",
          };
        });
        if (displayName) {
          returnData.fieldNames[fieldMap.field_label] = modifiedLookupFields;
        }
        if (apiName) {
          returnData.fieldNames[fieldMap.api_name] = modifiedLookupFields;
        }
        return "lookupFields";
      }

      returnData.fieldNames[entity][index].moduleName = entity;
      returnData.fieldNames[entity][index].fieldOriginType = "entity";
    });
    await Promise.all(promises);

    /**
     * After all finished return data
     */
    return {
      fieldNames: returnValues.includes("fields")
        ? flatten.includes("fields")
          ? flattenObj(returnData?.fieldNames)
          : returnData?.fieldNames
        : {},
      recordData: returnValues.includes("recordData")
        ? flatten.includes("recordData")
          ? flattenObj(returnData?.recordData)
          : returnData?.recordData
        : {},
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getFieldsAndData", error),
    };
  }
};

export async function getRecordDetailsSDK({ module, recordId }) {
  try {
    const recordResp = await ZOHO.CRM.API.getRecord({
      Entity: module,
      RecordID: recordId,
    });

    return { data: recordResp?.data?.[0], error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getRecordDetailsSDK", error),
    };
  }
}

export async function getRecordDetailsRestAPI({ module, recordId, accessToken, dataCenterUrl }) {
  try {
    const url = `${dataCenterUrl}/crm/v6/${module}/${recordId}`;
    var req_data = {
      params: {},
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);

    return {
      data: resp_json.data?.[0],
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getRecordDetailsRestAPI", error),
    };
  }
}

export async function getBulkRecordDetailsRestAPI({ accessToken, url }) {
  //url = "https://www.zohoapis.com/crm/v6/Leads?ids=4731441000017895165,4731441000017895072,4731441000017139216,4731441000016814042,4731441000016814018,4731441000016543060,4731441000016023225,4731441000015988066,4731441000015982001,4731441000015981001,4731441000015979036,4731441000015979001,4731441000015964002,4731441000014979008,4731441000014471064,4731441000014471026,4731441000014471010,4731441000014300111,4731441000014300071,4731441000014300031&fields=Full_Name,Testing_Field,test_new_field,Email,Email_3,field_mapping_email,field_mapping_email_1,Company_Email,Client_Email"
  try {
    var req_data = {
      params: {},
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);
    return {
      data: resp_json.data,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getBulkRecordDetailsRestAPI", error),
    };
  }
}

export async function getFileFields({ module, accessToken, dataCenterUrl }) {
  const url = `${dataCenterUrl}/crm/v6/settings/fields?module=${module}`;
  try {
    var req_data = {
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);
    const fields = await resp_json?.fields.filter((el) => el.data_type === "fileupload" || el.data_type === "imageupload");
    return {
      error: null,
      data: fields,
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getFileFields", error),
    };
  }
}

export async function getEmailFields({ module, accessToken, dataCenterUrl }) {
  try {
    const url = `${dataCenterUrl}/crm/v6/settings/fields?module=${module}`;
    var req_data = {
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);
    const emailFields = await resp_json?.fields.filter((el) => el.data_type === "email");
    return {
      error: null,
      data: emailFields,
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getEmailFields", error),
    };
  }
}

export async function getEmailAndFileFields({ module, accessToken, dataCenterUrl }) {
  try {
    const url = `${dataCenterUrl}/crm/v6/settings/fields?module=${module}`;
    var req_data = {
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);
    const emailFields = await resp_json?.fields.filter((el) => el.data_type === "email");
    const fileFields = await resp_json?.fields.filter((el) => el.data_type === "fileupload" || el.data_type === "imageupload");
    return {
      error: null,
      data: { emailFields, fileFields },
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getEmailAndFileFields", error),
    };
  }
}

export async function getRelatedListApis({ module, accessToken, dataCenterUrl }) {
  try {
    const url = `${dataCenterUrl}/crm/v6/settings/related_lists?module=${module}`;
    var req_data = {
      url,
      param_type: 1,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    };
    const resp = await ZOHO.CRM.HTTP.get(req_data);
    const resp_json = await JSON.parse(resp);
    const fArr = [
      "Notes",
      "Calls",
      "Tasks",
      "Events",
      "Products",
      "Calls_History",
      "Tasks_History",
      "Events_History",
      "Zoho_Subscriptions",
    ];
    const data = resp_json.related_lists.filter(
      (el) =>
        !!el?.module &&
        !!el.href &&
        !!el.customize_fields &&
        // !(el.type === "multiselectlookup") &&
        !fArr.includes(el.api_name) &&
        !el.api_name.includes("Cadences")
    );
    return {
      data,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getRelatedListApis", error),
    };
  }
}

export async function getRecordsFromRelatedList({ module, recordId, RelatedListAPI }) {
  try {
    const relatedListResp = await ZOHO.CRM.API.getRelatedRecords({
      Entity: module,
      RecordID: recordId,
      RelatedList: RelatedListAPI,
    });

    if (relatedListResp.statusText === "nocontent") {
      return { data: [], error: null };
    }

    if (!(relatedListResp.statusText === "nocontent")) {
      return { data: relatedListResp?.data, erroe: null };
    }
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getRecordsFromRelatedList", error),
    };
  }
}

export async function insertRecord({ moduleApiName, recordData }) {
  try {
    const resp = await ZOHO.CRM.API.insertRecord({
      Entity: moduleApiName,
      APIData: recordData,
    });
    return { data: resp, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("insertRecord", error),
    };
  }
}

export async function updateRecord({ moduleApiName, recordData }) {
  try {
    const resp = await ZOHO.CRM.API.updateRecord({
      Entity: moduleApiName,
      APIData: recordData,
    });
    return { data: resp, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("updateRecord", error),
    };
  }
}

export async function deleteRecord({ moduleApiName, recordId }) {
  try {
    const resp = await ZOHO.CRM.API.deleteRecord({
      Entity: moduleApiName,
      RecordID: recordId,
    });
    return { data: resp, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("deleteteRecord", error),
    };
  }
}

export async function getRecordsEmailAndLookupEmail({ module, dataCenterUrl, record_id, orgId, apiKey, connName }) {
  try {
    const data_config = {
      apiDomain: dataCenterUrl,
      entity: module,
      entityId: record_id + "",
      displayName: false,
      apiName: true,
      flatten: [],
      returnValues: ["recordData", "fields"],
      fetchAdditionalFieldTypes: ["entity", "lookup", "ownerlookup"],
    };
    // "recordData"
    const allFields = await axios.request({
      url: `${env.REACT_APP_ADMIN_SERVER_URL}/data/zoho/crm/getFieldsAndData`,
      method: "POST",
      headers: {
        orgid: orgId,
        apikey: apiKey,
        datacenterurl: dataCenterUrl,
        connname: connName,
      },
      data: data_config,
    });

    let emails = [];
    Object.keys(allFields?.fieldNames)?.map((module) => {
      allFields?.fieldNames?.[module]
        ?.filter((field) => field?.data_type === "email")
        .map((field) => {
          const email = allFields?.recordData?.module?.[field?.api_name];
          emails.push({ email, module });
          return;
        });
    });

    // const url = `${dataCenterUrl}/crm/v6/settings/fields?module=${module}`;
    // var req_data = {
    //   url,
    //   param_type: 1,
    //   headers: {
    //     Authorization: "Bearer " + accessToken,
    //   },
    // };
    // const resp = await ZOHO.CRM.HTTP.get(req_data);
    // const resp_json = await JSON.parse(resp);
    // const emailFields = await resp_json?.fields.filter(
    //   (el) => el.data_type === "email"
    // );
    // const fileFields = await resp_json?.fields.filter(
    //   (el) => el.data_type === "fileupload" || el.data_type === "imageupload"
    // );
    return {
      error: null,
      data: { allFields },
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getRecordsEmailAndLookupEmail", error),
    };
  }
}

export const record = {
  getFieldsAndData,
  getRelatedListApis,
  getRecordDetailsRestAPI,
  getBulkRecordDetailsRestAPI,
  getFileFields,
  getEmailFields,
  getEmailAndFileFields,
  getRecordsFromRelatedList,
  insertRecord,
  updateRecord,
  deleteRecord,
  getRecordsEmailAndLookupEmail,
};
